import { useState, useCallback } from 'react';
import promptService from '../../services/promptService';

/**
 * Custom hook for managing a single prompt
 * @param {string} tenantId - The tenant ID
 * @returns {Object} - Prompt state and actions
 */
const usePrompt = (tenantId) => {
	const [prompt, setPrompt] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [promptTypes, setPromptTypes] = useState([]);

	/**
	 * Fetch a single prompt by ID
	 * @param {string} promptId - The prompt ID to fetch
	 */
	const fetchPrompt = useCallback(
		async (promptId) => {
			if (!tenantId || !promptId) return null;

			setLoading(true);
			setError(null);

			try {
				const response = await promptService.getPrompt(tenantId, promptId);

				const formattedPrompt = {
					id: response.id,
					name: response.name,
					content: response.content,
					promptType: response.prompt_type_name,
					promptTypeId: response.prompt_type_id,
					version: response.prompt_version,
					latestVersion: response.latest_version_number,
					hasNewerVersion: response.has_newer_version,
					active: response.active,
					hierarchyLevel: response.hierarchy_level,
					createdAt: response.created_at,
					updatedAt: response.updated_at,
					_original: response,
				};

				setPrompt(formattedPrompt);
				return formattedPrompt;
			} catch (err) {
				console.error('Failed to fetch prompt:', err);
				setError('Failed to load prompt. Please try again.');
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[tenantId]
	);

	/**
	 * Create a new prompt
	 * @param {Object} promptData - The prompt data to create
	 */
	const createPrompt = useCallback(
		async (promptData) => {
			if (!tenantId) return null;

			setLoading(true);
			setError(null);

			try {
				const newPrompt = await promptService.createPrompt(
					tenantId,
					promptData
				);
				return newPrompt;
			} catch (err) {
				console.error('Failed to create prompt:', err);
				setError('Failed to create prompt. Please try again.');
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[tenantId]
	);

	/**
	 * Update an existing prompt
	 * @param {string} promptId - The prompt ID to update
	 * @param {Object} promptData - The updated prompt data
	 */
	const updatePrompt = useCallback(
		async (promptId, promptData) => {
			if (!tenantId || !promptId) return null;

			setLoading(true);
			setError(null);

			try {
				const response = await promptService.updatePrompt(
					tenantId,
					promptId,
					promptData
				);

				// Format the updated prompt to match our expected format
				const updatedPrompt = {
					id: response.id,
					name: response.name,
					content: response.content,
					promptType: response.prompt_type_name,
					promptTypeId: response.prompt_type_id,
					version: response.prompt_version,
					latestVersion: response.latest_version_number,
					hasNewerVersion: response.has_newer_version,
					active: response.active,
					hierarchyLevel: response.hierarchy_level,
					createdAt: response.created_at,
					updatedAt: response.updated_at,
					_original: response,
				};

				// Update the prompt in state if it matches the current prompt
				if (prompt && prompt.id === promptId) {
					setPrompt(updatedPrompt);
				}

				return updatedPrompt;
			} catch (err) {
				console.error('Failed to update prompt:', err);
				setError('Failed to update prompt. Please try again.');
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[tenantId, prompt]
	);

	/**
	 * Fetch all prompt types
	 */
	const fetchPromptTypes = useCallback(async () => {
		if (!tenantId) return [];

		setLoading(true);
		setError(null);

		try {
			const types = await promptService.getPromptTypes(tenantId);
			setPromptTypes(types);
			return types;
		} catch (err) {
			console.error('Failed to fetch prompt types:', err);
			setError('Failed to load prompt types. Please try again.');
			throw err;
		} finally {
			setLoading(false);
		}
	}, [tenantId]);

	/**
	 * Toggle the active status of a prompt
	 * @param {string} promptId - The ID of the prompt to toggle
	 * @param {boolean} active - The new active status
	 * @returns {Promise<Object>} The updated prompt
	 */
	const toggleActive = useCallback(
		async (promptId, active) => {
			if (!tenantId || !promptId) {
				throw new Error('Tenant ID and prompt ID are required');
			}

			setLoading(true);
			setError(null);

			try {
				const updatedPrompt = await promptService.toggleActive(
					tenantId,
					promptId,
					active
				);

				// Update the local prompt state if we're managing this prompt
				if (prompt?.id === promptId) {
					setPrompt((prev) => ({
						...prev,
						active: updatedPrompt.active,
						version: updatedPrompt.prompt_version,
						_original: {
							...prev._original,
							active: updatedPrompt.active,
							prompt_version: updatedPrompt.prompt_version,
						},
					}));
				}

				return updatedPrompt;
			} catch (err) {
				console.error('Failed to update prompt status:', err);
				setError('Failed to update prompt status. Please try again.');
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[tenantId, prompt?.id]
	);

	/**
	 * Delete a prompt
	 * @param {string} promptId - The ID of the prompt to delete
	 * @returns {Promise<void>}
	 */
	const deletePrompt = useCallback(
		async (promptId) => {
			if (!tenantId || !promptId) {
				throw new Error('Tenant ID and prompt ID are required');
			}

			setLoading(true);
			setError(null);

			try {
				await promptService.deletePrompt(tenantId, promptId);

				// Clear the prompt if we're managing the deleted one
				if (prompt?.id === promptId) {
					setPrompt(null);
				}

				// Return the deleted prompt ID for parent components to handle
				return promptId;
			} catch (err) {
				console.error('Failed to delete prompt:', err);
				setError('Failed to delete prompt. Please try again.');
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[tenantId, prompt?.id]
	);

	return {
		prompt,
		loading,
		error,
		promptTypes,
		fetchPrompt,
		createPrompt,
		updatePrompt,
		toggleActive,
		deletePrompt,
		fetchPromptTypes,
	};
};

export default usePrompt;
